import { useEffect, useMemo, useState } from 'react';

import { UserCard } from 'components/UI/UserCard';
import {
	SubscriptionUser,
	UpdateEnterpriseUser,
	UserLicenseLabels,
	UserLicenseModel,
	UserRole
} from 'store/account/subscription';

import { DeleteSubscriptionUserModal } from '../DeleteSubscriptionUserModal';
import { UserRoleSelector } from '../../Utils';
import { EditEnterpriseUserForm } from './EditEnterpriseUserForm';
import { ResetSubscriptionUserPasswordModal } from '../ResetSubscriptionUserPasswordModal';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { Button } from 'components/UI/Interactables/Button';
import { Modal } from 'components/UI/Modal';
import { Spacer } from 'components/UI/Spacer';
import {
	getAddUserValidationSchema,
	getLicenseModelIdFromSubscriptionQuantities
} from 'helpers/subscription';
import {
	useTranslation,
	useUpdateEnterpriseUser,
	useChangeUserLicenseModel,
	useLicenseModels,
	useSubscription,
	useSubscriptionBundleFlag
} from 'hooks/store';
import { useReactForm } from 'hooks/ui';
import { useCompletedAction, usePrevious } from 'hooks/utils';

export enum EditSubscriptionUserTab {
	Information = 0,
	License = 1,
	Roles = 2
}

export type FormFields = Omit<
	SubscriptionUser,
	'imageURL' | 'subscriptionStatus' | 'isOwner' | 'licenseModel'
> & { licenseModel: UserLicenseModel };

interface Props {
	user: SubscriptionUser | null;
	onClose: () => void;
	selectedTab?: EditSubscriptionUserTab;
}

export function EditSubscriptionUserModal({
	user,
	onClose,
	selectedTab = EditSubscriptionUserTab.Information
}: Props) {
	const { translate } = useTranslation();

	const { subscriptionBundleFlag } = useSubscriptionBundleFlag();

	const [deleteUser, setDeleteUser] = useState(false);

	const [resetPassword, setResetPassword] = useState(false);

	const [currentTab, setCurrentTab] = useState<number>(selectedTab);

	const [{ loading: loadingUpdateEnterpriseUser }, updateEnterpriseUser] =
		useUpdateEnterpriseUser();

	const [{ loading: changingLicenseType, error: errorChangingLicenseType }, changeLicenseType] =
		useChangeUserLicenseModel();

	const [
		{
			data: {
				availableLicenseOptions,
				allLicenseOptions,
				licenseQuantitiesById,
				licenseModelsMetadataById
			}
		}
	] = useLicenseModels();

	const licenseOptions = useMemo(
		() =>
			user && user.isOwner
				? availableLicenseOptions.concat([
						{
							label: translate(
								() => UserLicenseLabels[UserLicenseModel.OneOwnedOneShared]
							),
							value: UserLicenseModel.OneOwnedOneShared
						}
				  ])
				: availableLicenseOptions,
		[availableLicenseOptions, user]
	);

	const [
		{
			data: {
				subscriptionTypes: { isLedidiEnterprise },
				userTypesAndRoles: { ledidiEnterprise }
			}
		}
	] = useSubscription();

	const hasLedidiEnterpriseAdminRole =
		ledidiEnterprise.isOrganizationAdmin ||
		ledidiEnterprise.isSuperAdmin ||
		ledidiEnterprise.isAdmin;

	const initialValues: FormFields = {
		userid: user ? user.userid : '',
		emailAddress: user ? user.emailAddress : '',
		userFirstName: user ? user.userFirstName : '',
		userSirName: user ? user.userSirName : '',
		country: user ? user.country : '',
		city: user ? user.city : '',
		phoneNumber: user ? user.phoneNumber : '',
		workplace: user ? user.workplace : '',
		position: user ? user.position : '',
		department: user ? user.department : '',
		userRole: user ? user.userRole : undefined,
		licenseModel: user ? user.licenseModel : UserLicenseModel.Full
	};

	const editUserValidationSchema = getAddUserValidationSchema({ translate });

	const {
		Form,
		FormProvider,
		formProviderProps,
		Controller,
		setValue,
		handleSubmit,
		isDirty: formHasChanges,
		control
	} = useReactForm({
		initialValues,
		validationSchema: editUserValidationSchema
	});

	const handleUpdateUser = handleSubmit(data => {
		if (user) {
			if (isLedidiEnterprise) {
				if (subscriptionBundleFlag) {
					const licenseModel = user.licenseModel;
					let licenseModelId;

					if (licenseModel && licenseQuantitiesById)
						licenseModelId = getLicenseModelIdFromSubscriptionQuantities(
							licenseModel,
							licenseQuantitiesById,
							licenseModelsMetadataById
						);
					updateEnterpriseUser({
						...data,
						licenseModelId: licenseModelId,
						emailAddress: user.emailAddress,
						workplace: user.workplace ?? '',
						userRole: data.userRole ?? UserRole.User
					});
				} else {
					updateEnterpriseUser({
						...data,
						emailAddress: user.emailAddress,
						workplace: user.workplace ?? '',
						userRole: data.userRole ?? UserRole.User
					});
				}
			} else {
				changeLicenseType({
					licenseModel: user.licenseModel,
					userId: user.userid
				});
			}
		}
	});

	useCompletedAction(changingLicenseType, errorChangingLicenseType, closeModal);

	useEffect(() => {
		if (user) {
			setValue('userid', user.userid, {
				shouldDirty: true
			});
			setValue('emailAddress', user.emailAddress, {
				shouldDirty: false
			});
			setValue('userFirstName', user.userFirstName, {
				shouldDirty: false
			});
			setValue('userSirName', user.userSirName, {
				shouldDirty: false
			});
			setValue('country', user.country, {
				shouldDirty: false
			});
			setValue('phoneNumber', user.phoneNumber, {
				shouldDirty: false
			});
			setValue('workplace', user.workplace, {
				shouldDirty: false
			});
			setValue('position', user.position, {
				shouldDirty: false
			});
			setValue('department', user.department, {
				shouldDirty: false
			});
			setValue('licenseModel', user.licenseModel, {
				shouldDirty: false
			});
			setValue('userRole', user.userRole, {
				shouldDirty: false
			});
		}
	}, [user]);

	function getLicenseOption(value: string) {
		return allLicenseOptions.find(license => license.value === value);
	}

	function onConfirm() {
		if (formHasChanges) handleUpdateUser();
		else closeModal();
	}

	function closeModal() {
		setCurrentTab(EditSubscriptionUserTab.Information);
		onClose();
	}

	function onDeleteUserClosed(deleted?: boolean) {
		setDeleteUser(false);
		if (deleted) closeModal();
	}

	const userCardComponent = useMemo(() => {
		return user ? (
			<UserCard.Details
				userId={user.userid}
				userData={{
					emailAddress: user.emailAddress,
					userFirstName: user.userFirstName,
					userSirName: user.userSirName,
					position: user.position,
					phoneNumber: user.phoneNumber,
					city: user.city,
					country: user.country,
					workplace: user.workplace ?? '',
					department: user.department
				}}
				showDetails={!isLedidiEnterprise}
			/>
		) : undefined;
	}, [user, isLedidiEnterprise]);
	/**
	 *
	 * @param name Close modal after loading is finished in case User is being updated
	 * @param value
	 */
	const prevChangeLicenseLoading = usePrevious(changingLicenseType);
	const prevLoadingUpdateEnterpriseUser = usePrevious(loadingUpdateEnterpriseUser);
	useEffect(() => {
		if (
			(prevChangeLicenseLoading && !changingLicenseType) ||
			(prevLoadingUpdateEnterpriseUser && !loadingUpdateEnterpriseUser)
		)
			closeModal();
	}, [changingLicenseType, loadingUpdateEnterpriseUser]);

	function handleValue(name: keyof Omit<UpdateEnterpriseUser, 'licenseModelId'>, value: string) {
		setValue(name, value, { shouldDirty: true });
	}

	const tabsLabels = [
		translate(dict => dict.accountUM.editSubscriptionUser.tabInformation),
		translate(dict => dict.accountUM.editSubscriptionUser.tabLicense)
	];

	if (hasLedidiEnterpriseAdminRole) {
		tabsLabels.push(translate(dict => dict.accountUM.editSubscriptionUser.tabRoles));
	}

	return (
		<Modal
			visible={!!user}
			title={translate(dict => dict.accountUM.editSubscriptionUser.editModalTitle)}
			primary={{
				label: formHasChanges
					? translate(dict => dict.buttons.update)
					: translate(dict => dict.buttons.done),
				loading: changingLicenseType || loadingUpdateEnterpriseUser,
				warning: true,
				onClick: changingLicenseType || loadingUpdateEnterpriseUser ? undefined : onConfirm
			}}
			neutral={{
				label: translate(dict => dict.buttons.cancel),
				onClick: changingLicenseType || loadingUpdateEnterpriseUser ? undefined : closeModal
			}}
			tabs={{
				labels: tabsLabels,
				active: currentTab,
				onClick: (tabIndex: number) => setCurrentTab(tabIndex)
			}}
			onClose={closeModal}
			close
		>
			{user && (
				<>
					{currentTab === EditSubscriptionUserTab.Information &&
						(isLedidiEnterprise ? (
							<>
								{userCardComponent}
								<FormProvider {...formProviderProps}>
									<Form onSubmit={handleUpdateUser}>
										<EditEnterpriseUserForm
											onResetPassword={() => setResetPassword(true)}
										/>
									</Form>
								</FormProvider>
							</>
						) : (
							userCardComponent
						))}

					{currentTab === EditSubscriptionUserTab.License && (
						<>
							<Spacer size={s => s.m} />
							<Form onSubmit={handleUpdateUser}>
								<Controller
									control={control}
									name="licenseModel"
									render={({ field: { value } }) => (
										<CreatableSelect
											value={getLicenseOption(value)}
											label={translate(
												({ accountUM }) =>
													accountUM.userDetails.licenseModelSelect
											)}
											items={licenseOptions}
											onValueSelected={value =>
												setValue(
													'licenseModel',
													value as UserLicenseModel,
													{
														shouldDirty: true
													}
												)
											}
											canClear={false}
											scrollIntoView
											disabled={
												!subscriptionBundleFlag || !licenseOptions.length
											}
											required
										/>
									)}
								/>
							</Form>
							<Spacer size={s => s.m} />
							{!isLedidiEnterprise && (
								<Button
									title={translate(
										dict =>
											dict.accountUM.editSubscriptionUser.removeUserLicense
									)}
									variant={v => v.outline}
									onClick={() => {
										setDeleteUser(true);
									}}
								/>
							)}
						</>
					)}

					{currentTab === EditSubscriptionUserTab.Roles && (
						<>
							<Spacer size={s => s.m} />
							<Form onSubmit={handleUpdateUser}>
								<Controller
									control={control}
									name="userRole"
									render={({ field: { value: selectedUserRole } }) => (
										<UserRoleSelector
											selected={selectedUserRole as UserRole}
											onSelect={selected => handleValue('userRole', selected)}
											isOwner={user.isOwner}
										/>
									)}
								/>
							</Form>
						</>
					)}

					{user.userid && deleteUser && (
						<DeleteSubscriptionUserModal
							onClose={onDeleteUserClosed}
							userId={user.userid}
							userName={user.userFirstName + ' ' + user.userSirName}
							licenseModel={user.licenseModel}
						/>
					)}

					{user && resetPassword && (
						<ResetSubscriptionUserPasswordModal
							onClose={() => setResetPassword(false)}
							userId={resetPassword ? user.userid : null}
						/>
					)}
				</>
			)}
		</Modal>
	);
}
